import { Component, ComponentInterface, Element, Event, EventEmitter, Listen, Prop, Watch } from '@stencil/core';
import { DEFAULT_WORKBENCH_THEME, WorkbenchTheme } from '../../shared/themes';
import { applyClass } from '../../utils/apply-class';
import { getRandomId } from '../../utils/get-random-id';
import { querySelectorAllAsArray } from '../../utils/query-selector-all-as-array';
import { connectTabAndContent } from './tabs.util';

@Component({
  tag: 'wb-tabs',
  styleUrl: 'tabs.scss',
  shadow: false,
})
export class TabsComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** the color scheme */
  @Prop({ reflect: true })
  public theme: WorkbenchTheme = DEFAULT_WORKBENCH_THEME;

  /** Namespace for element and all its children */
  @Prop({ reflect: true })
  public namespace: string = getRandomId();

  /** Name of the selected tab */
  @Prop({ reflect: true, mutable: true })
  public selected!: string;

  /** Event emitted when selected tab has changed */
  @Event()
  public wbchange!: EventEmitter<string>;

  private tabBarEl!: HTMLWbTabBarElement;

  public connectedCallback(): void {
    this.tabBarEl = this.el.querySelector<HTMLWbTabBarElement>('wb-tab-bar');
    this.connectTabsAndContents();
  }

  public componentDidLoad(): void {
    this.selectTabWithName(this.selected, null, true);
  }

  @Watch('selected')
  public onSelectedChange(newSelected: string, prevSelected: string): void {
    this.selectTabWithName(newSelected, prevSelected, false);
  }

  @Watch('theme')
  public onThemeChange(): void {
    this.tabBarEl.updateTheme();
  }

  @Listen('click')
  public onClick(event: MouseEvent): void {
    const composedPath = event.composedPath();
    const tabEl = composedPath.find((el) => (el as HTMLElement).nodeName === 'WB-TAB') as HTMLWbTabElement;

    if (tabEl) {
      this.selected = tabEl.name;
    }
  }

  public selectTabWithName(newSelected: string, prevSelected: string, isInitialSelection: boolean): void {
    // reset, when user tries to clear selection after a selection has already been made
    if (newSelected == null) {
      // note that prevSelected can be falsey (null/undefined) during the runtime of the component
      if (isInitialSelection === false && prevSelected) {
        this.reset();
      }

      return;
    }

    const newSelectedTabEl = this.tabEls.find((el) => el.name === newSelected);

    // change nothing, when user tries to select disabled or non-existing tab
    if (!newSelectedTabEl || newSelectedTabEl.disabled) {
      return;
    }

    this.selected = newSelectedTabEl.name;
    this.tabBarEl.selectElement(newSelectedTabEl);
    this.tabEls.forEach((el: HTMLWbTabElement) => el.setAttribute('aria-selected', String(el === newSelectedTabEl)));
    this.tabContentEls.forEach((el) => applyClass(el, 'wb-tab-content--selected', el.name === this.selected));

    // not triggering wbchange on initial selection and if same selection like before
    if (isInitialSelection || this.selected === prevSelected) {
      return;
    }

    this.wbchange.emit(this.selected);
  }

  private reset(): void {
    // note: this.selected is always null/undefined at this point
    this.tabBarEl.reset();
    this.tabEls.forEach((el: HTMLWbTabElement) => el.setAttribute('aria-selected', 'false'));
    this.tabContentEls.forEach((el) => applyClass(el, 'wb-tab-content--selected', false));
    this.wbchange.emit(this.selected);
  }

  private get tabEls(): Array<HTMLWbTabElement> {
    return querySelectorAllAsArray<HTMLWbTabElement>(this.el, 'wb-tab');
  }

  private get tabContentEls(): Array<HTMLWbTabContentElement> {
    return querySelectorAllAsArray<HTMLWbTabContentElement>(this.el, 'wb-tab-content');
  }

  private connectTabsAndContents(): void {
    this.tabEls.forEach((tabEl) => {
      const tabContentEl = this.tabContentEls.find((tabContentEl) => tabContentEl.name === tabEl.name);

      if (tabContentEl) {
        connectTabAndContent(this.namespace, tabEl, tabContentEl);
      }
    });
  }
}
